<script setup lang="ts">
import { dateRange as dateRangeFn } from '~/helpers/formatDateRange'

import type { HouseDetails } from '~/types/house/types'

const { t, locale } = useI18n()
const { isMobile } = useBreakpoint()
const { booking } = useHouseInquiryBooking()

defineProps<{
  hasSelectedPeriod: boolean
  house: HouseDetails
  pricesToDisplay: string
  showPublicTaxesInfo: boolean
}>()

const emits = defineEmits<{
  'handle-modify-dates': []
}>()

const dateRange = computed(() =>
  dateRangeFn({
    i18n: { t, locale },
    startDate: booking.value.startDate,
    endDate: booking.value.endDate,
  }),
)
</script>

<template>
  <BaseRow>
    <BaseCol>
      <div class="flex items-center justify-between">
        <h2
          :class="{
            'mb-6 text-lg': isMobile,
            'mb-2 text-4xl': !isMobile,
          }"
        >
          <span v-if="isMobile" class="text-lg">
            {{ $t('inquiry.yourRequestFor') }}
          </span>
          {{ house.name }}
        </h2>
      </div>

      <p
        v-if="!house.displayPrices"
        class="mb-6 font-medium"
        data-testid="inquiry-date-picker__price-unavailable"
      >
        {{ $t('house.price_upon_request') }}
      </p>
      <p
        v-else-if="!booking.endDate"
        class="font-bold"
        :class="{
          'mb-1': showPublicTaxesInfo,
          'mb-6': !showPublicTaxesInfo,
        }"
        data-testid="inquiry-date-picker__price-range"
      >
        {{ pricesToDisplay }}
      </p>

      <p
        v-if="showPublicTaxesInfo && !hasSelectedPeriod"
        class="mb-6 text-sm font-normal italic text-gray-500"
        data-testid="inquiry-date-picker__local-taxes"
      >
        {{ $t('inquiry.localTaxesIncluded') }}
      </p>
    </BaseCol>
  </BaseRow>

  <div
    v-if="isMobile"
    :class="[
      { 'border-b pb-6': booking.startDate && booking.endDate },
      'mb-6 border-gray-200 text-md',
    ]"
  >
    <span class="font-medium">{{ $t('house.dates') }}</span>
    <div class="flex justify-between">
      <span v-if="dateRange">
        {{ dateRange }}
      </span>
      <button
        class="underline"
        type="button"
        @click="emits('handle-modify-dates')"
      >
        {{ $t(dateRange ? 'global.modify' : 'global.placeholders.select') }}
      </button>
    </div>
  </div>
</template>
